/**
 * 10.6E — LUZ DE BORDA (fresnel) por material.
 *
 * O Caçador lia como um borrão escuro: silhueta preta sobre chão escuro, sem nada separando
 * um do outro. A solução óbvia seria contorno (casco invertido), mas o Hunter tem ~46 malhas
 * e cada criatura tem 36–45 — dobraria os draw calls no celular.
 *
 * Isto faz o mesmo trabalho de graça: injeta um termo de FRESNEL no emissivo do material,
 * então a borda de cada peça acende sozinha, vista de qualquer ângulo. Nenhum objeto novo,
 * nenhum draw call novo — só uma variante de shader por material tratado.
 *
 * Injetado depois de `emissivemap_fragment`, onde `normal` (já com flat shading) e
 * `vViewPosition` (vetor fragmento→câmera) existem no shader padrão do three.js.
 */
import * as THREE from "three";

export interface RimOptions {
  color: number;
  /** Quanto a borda é fina: maior = só a beirada acende. */
  power?: number;
  strength?: number;
}

/** Aplica (ou reaplica) a luz de borda num MeshStandardMaterial. Idempotente. */
export function applyRim(mat: THREE.MeshStandardMaterial, opts: RimOptions): void {
  const u = (mat.userData.rim ??= {
    uRimColor: { value: new THREE.Color(opts.color) },
    uRimPower: { value: opts.power ?? 2.6 },
    uRimStrength: { value: opts.strength ?? 1.15 },
  }) as { uRimColor: { value: THREE.Color }; uRimPower: { value: number }; uRimStrength: { value: number } };
  u.uRimColor.value.setHex(opts.color);
  u.uRimPower.value = opts.power ?? 2.6;
  u.uRimStrength.value = opts.strength ?? 1.15;
  if (mat.userData.rimPatched === true) return;
  mat.userData.rimPatched = true;
  // onBeforeCompile não entra na chave de cache de programa do three.js: sem isto um
  // material COM rim poderia reaproveitar (ou emprestar) o programa de um material SEM rim
  // que tivesse os mesmos parâmetros.
  mat.customProgramCacheKey = () => "vhRim";
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uRimColor = u.uRimColor;
    shader.uniforms.uRimPower = u.uRimPower;
    shader.uniforms.uRimStrength = u.uRimStrength;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "void main() {",
        "uniform vec3 uRimColor;\nuniform float uRimPower;\nuniform float uRimStrength;\nvoid main() {",
      )
      .replace(
        "#include <emissivemap_fragment>",
        `#include <emissivemap_fragment>
        float vhRim = 1.0 - abs(dot(normalize(normal), normalize(vViewPosition)));
        totalEmissiveRadiance += uRimColor * pow(clamp(vhRim, 0.0, 1.0), uRimPower) * uRimStrength;`,
      );
  };
  mat.needsUpdate = true;
}
